import { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../store';
import { useAblyClient } from '../contexts/AblyContext';
import { SET_SETTINGS } from '../store/constants';
import { store } from '../store';
import debug from '../utilities/debug';
import type { RootState } from '../store';
import config from '../config';

/**
 * Subscribes to the Ably maintenance mode channel.
 *
 * Channel: system_maintenance_{stage}
 * Event:   maintenance-mode-update
 *
 * When a message arrives the settings slice is updated in Redux so
 * any component reading state.session.settings.maintenance_mode
 * immediately reflects the change — same as the web implementation.
 */
const useMaintenanceMode = () => {
  const dispatch = useAppDispatch();
  const { client } = useAblyClient();
  const settings = useSelector((state: RootState) => state.session.settings);
  const stage = config.env || 'development';

  const handleUpdate = useCallback((message: any) => {
    debug.log('ably', 'Maintenance mode update received', message.data);
    const currentSettings = store.getState()?.session?.settings || {};
    dispatch({
      type: SET_SETTINGS,
      payload: {
        ...currentSettings,
        maintenance_mode: message.data.maintenance_mode,
        maintenance_mode_title: message.data.maintenance_mode_title,
        maintenance_mode_message: message.data.maintenance_mode_message,
      },
    });
  }, [dispatch]);

  useEffect(() => {
    if (!client) return;

    const channelName = `system_maintenance_${stage}`;
    const channel = client.channels.get(channelName);
    channel.subscribe('maintenance-mode-update', handleUpdate);
    debug.log('ably', `Subscribed to ${channelName}`);

    return () => {
      channel.unsubscribe('maintenance-mode-update', handleUpdate);
      debug.log('ably', `Unsubscribed from ${channelName}`);
    };
  }, [client, stage, handleUpdate]);

  return {
    maintenanceMode: settings?.maintenance_mode || false,
    maintenanceTitle: settings?.maintenance_mode_title as string | undefined,
    maintenanceMessage: settings?.maintenance_mode_message as string | undefined,
  };
};

export default useMaintenanceMode;
